import React from "react";
import {
  Alert,
  Box,
  Container,
  Link,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  StatusIndicatorProps,
} from "@amzn/awsui-components-react";

import {
  CatalogIssue,
  CatalogIssueSeverity,
} from "../../../from-server/asin-central/catalog-issues";
import { CatalogIssueDetailState } from "./useCatalogIssues";

export interface CatalogIssueDetailPaneProps {
  readonly detail: CatalogIssueDetailState;
}

/**
 * Maps an issue severity to a StatusIndicator type. Any unrecognised
 * severity (including the explicit UNKNOWN) falls back to a neutral
 * indicator so it can never break rendering.
 */
export function severityIndicatorType(
  severity: CatalogIssueSeverity
): StatusIndicatorProps.Type {
  switch (severity) {
    case "ERROR":
      return "error";
    case "WARNING":
      return "warning";
    case "INFO":
      return "info";
    default:
      return "stopped";
  }
}

function severityLabel(severity: CatalogIssueSeverity): string {
  switch (severity) {
    case "ERROR":
      return "Error";
    case "WARNING":
      return "Warning";
    case "INFO":
      return "Info";
    default:
      return "Unknown";
  }
}

export const CatalogIssueItem: React.FC<{ readonly issue: CatalogIssue }> = ({
  issue,
}) => {
  return (
    <SpaceBetween size="xxs" data-testid="catalog-issue-item">
      <SpaceBetween direction="horizontal" size="xs">
        <StatusIndicator type={severityIndicatorType(issue.severity)}>
          {severityLabel(issue.severity)}
        </StatusIndicator>
        <Box variant="strong" data-testid="catalog-issue-code">
          {issue.issueCode}
        </Box>
      </SpaceBetween>
      {issue.description !== undefined && issue.description !== "" && (
        <Box variant="p" data-testid="catalog-issue-description">
          {issue.description}
        </Box>
      )}
      {issue.attribute !== undefined && issue.attribute !== "" && (
        <Box
          variant="p"
          color="text-body-secondary"
          data-testid="catalog-issue-attribute"
        >
          Attribute: {issue.attribute}
        </Box>
      )}
      <Link
        external
        href={issue.selectionCentralUrl}
        data-testid="catalog-issue-selection-central-link"
      >
        View in Selection Central
      </Link>
    </SpaceBetween>
  );
};

/**
 * Issue Details pane for the ASIN row the user clicked. The detail is
 * fetched lazily on selection; this pane renders its loading, failure and
 * degraded-dependency states without affecting the rest of the tab.
 */
export const CatalogIssueDetailPane: React.FC<CatalogIssueDetailPaneProps> = ({
  detail,
}) => {
  if (detail.status === "idle") {
    return null;
  }

  let content: React.ReactNode;
  if (detail.status === "loading") {
    content = (
      <Box textAlign="center" padding={{ vertical: "l" }}>
        <Spinner size="normal" />
      </Box>
    );
  } else if (detail.status === "failed") {
    content = (
      <Alert type="error" data-testid="catalog-issue-detail-error">
        Failed to load issue details for {detail.asin}. Try selecting the row
        again.
      </Alert>
    );
  } else if (
    detail.result === undefined ||
    detail.result.dependencyStatus === "UNAVAILABLE" ||
    detail.result.dependencyStatus === "LIMIT_EXCEEDED"
  ) {
    content = (
      <Alert type="warning" data-testid="catalog-issue-detail-unavailable">
        {detail.result !== undefined && detail.result.warnings.length > 0
          ? detail.result.warnings.join(" ")
          : `Issue details for ${
              detail.asin ?? "this ASIN"
            } could not be retrieved from the catalog. Try again later.`}
      </Alert>
    );
  } else {
    content = (
      <SpaceBetween size="m">
        {detail.result.dependencyStatus === "PARTIAL" &&
          detail.result.warnings.length > 0 && (
            <Alert type="warning" data-testid="catalog-issue-detail-partial">
              {detail.result.warnings.join(" ")}
            </Alert>
          )}
        {detail.result.issues.length === 0 ? (
          <Box color="text-body-secondary">No issues found for this ASIN.</Box>
        ) : (
          detail.result.issues.map((issue, index) => (
            <CatalogIssueItem
              key={`${issue.issueCode}-${index}`}
              issue={issue}
            />
          ))
        )}
      </SpaceBetween>
    );
  }

  return (
    <Container
      data-testid="catalog-issue-detail-pane"
      header={<Box variant="h4">{detail.asin}</Box>}
    >
      {content}
    </Container>
  );
};

export default CatalogIssueDetailPane;
